/**
 * validateIntent — the single gate for human and bot intents (§3 "Validation covers…").
 * Pure: never mutates state. `step` re-validates each intent at application time.
 */
import { BALANCE } from './balance.js';
import {
  aircraftOf,
  allied,
  baseCapacity,
  betrayalLocked,
  countOwned,
  inCeasefire,
  docked,
  inFlightCount,
  isAirborne,
  player,
  shipActive,
  structureActive,
} from './entities.js';
import { dist, hexDist, landHexAt, nearestWater } from './hex.js';
import { moveTarget } from './systems/naval.js';
import type { BuildKind, Entity, Intent, IntentError, IntentResult, MatchState, PlayerId, PlayerState } from './types.js';

const OK: IntentResult = { ok: true };
const fail = (err: IntentError): IntentResult => ({ ok: false, err });

const BUILD_KINDS: readonly BuildKind[] = ['radar', 'sam', 'silo', 'city', 'airbase', 'sub', 'carrier'];

export function buildCost(kind: BuildKind): number {
  return BALANCE[kind].cost;
}
export function buildTicks(kind: BuildKind): number {
  return BALANCE[kind].buildTicks;
}
export function buildCap(kind: BuildKind): number {
  return kind === 'city' ? Infinity : BALANCE[kind].cap;
}
/** Credits to upgrade a city from `level` to level + 1 (`BALANCE.city.upgradeCost[level − 1]`; Infinity at max level). */
export function cityUpgradeCost(level: number): number {
  return BALANCE.city.upgradeCost[level - 1] ?? Infinity;
}

const finite = (...xs: unknown[]) => xs.every((x) => typeof x === 'number' && Number.isFinite(x));
const isInt = (x: unknown) => typeof x === 'number' && Number.isInteger(x);

function inBounds(state: MatchState, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x <= state.world.width && y <= state.world.height;
}

function owned(state: MatchState, p: PlayerId, id: unknown): Entity | IntentError {
  if (!isInt(id)) return 'badIntent';
  const e = state.entities.get(id as number);
  if (!e) return 'notFound';
  if (e.owner !== p) return 'notOwner';
  return e;
}

/** Point target rules shared by ICBM / SLBM / bomber strikes. */
function strikeTargetError(state: MatchState, p: PlayerId, x: number, y: number): IntentError | null {
  if (!inBounds(state, x, y)) return 'badTarget';
  if (state.tick < BALANCE.phases.releaseTick) return 'releaseLock';
  const h = landHexAt(state.geo, x, y);
  if (h >= 0) {
    const o = state.owner[h];
    if (o !== 0 && o !== p && allied(state, p, o)) return 'allyTarget';
    if (o !== 0 && betrayalLocked(state, p, o)) return 'betrayalLock';
    if (o !== 0 && o !== p && inCeasefire(state, p, o)) return 'ceasefire';
  }
  return null;
}

export function validateIntent(state: MatchState, playerId: PlayerId, intent: Intent): IntentResult {
  if (state.result) return fail('matchOver');
  const p = player(state, playerId);
  if (!p) return fail('unknownPlayer');
  if (p.eliminatedTick >= 0) return fail('eliminated');
  if (!intent || typeof intent !== 'object') return fail('badIntent');
  const N = state.world.landCount;

  switch (intent.type) {
    case 'focus': {
      if (intent.hex !== null && (!isInt(intent.hex) || intent.hex < 0 || intent.hex >= N)) return fail('badHex');
      if (state.tick - p.focusSetTick < BALANCE.creep.focusCooldownTicks) return fail('cooldown');
      return OK;
    }

    case 'build':
      return validateBuild(state, p, intent.kind, intent.hex);

    case 'cancel': {
      const e = owned(state, p.id, intent.unitId);
      if (typeof e === 'string') return fail(e);
      if (e.kind === 'city' && structureActive(state, e)) return e.upgradeDoneTick > 0 ? OK : fail('notBuilding');
      if ('buildDoneTick' in e && e.buildDoneTick > state.tick) return OK;
      if ((e.kind === 'fighter' || e.kind === 'bomber') && e.phase === 'building') return OK;
      return fail('notBuilding');
    }

    case 'upgradeCity': {
      const e = owned(state, p.id, intent.unitId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'city') return fail('badKind');
      if (!structureActive(state, e)) return fail('notReady');
      if (e.upgradeDoneTick > 0) return fail('busy');
      if (e.level >= BALANCE.city.maxLevel) return fail('maxLevel');
      if (p.credits < cityUpgradeCost(e.level)) return fail('credits');
      return OK;
    }

    case 'brace': {
      const e = owned(state, p.id, intent.cityId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'city') return fail('badKind');
      if (!structureActive(state, e)) return fail('notReady');
      if (state.tick < e.braceReadyTick) return fail('cooldown');
      if (p.credits < BALANCE.brace.cost) return fail('credits');
      return OK;
    }

    case 'samPriority': {
      const e = owned(state, p.id, intent.samId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'sam') return fail('badKind');
      if (intent.targetId === null) return OK;
      if (!isInt(intent.targetId)) return fail('badIntent');
      const t = state.entities.get(intent.targetId);
      if (!t || (t.kind !== 'icbm' && t.kind !== 'slbm')) return fail('notFound');
      // only a missile the player's team holds a track for (no fog leak through the ack)
      if (!(t.trackMask & state.vision.teamMask[p.id])) return fail('notFound');
      if (allied(state, p.id, t.owner)) return fail('badTarget');
      return OK;
    }

    case 'samMode': {
      const e = owned(state, p.id, intent.samId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'sam') return fail('badKind');
      if (typeof intent.silent !== 'boolean') return fail('badIntent');
      return OK;
    }

    case 'buyMissile': {
      const e = owned(state, p.id, intent.siloId);
      if (typeof e === 'string') return fail(e);
      if (!isInt(intent.count) || intent.count < 1) return fail('badIntent');
      if (e.kind === 'silo') {
        if (!structureActive(state, e)) return fail('notReady');
        if (e.loaded + e.queued + intent.count > BALANCE.silo.magazine) return fail('full');
        if (p.credits < BALANCE.silo.missileCost * intent.count) return fail('credits');
        return OK;
      }
      if (e.kind === 'sub') {
        // a sub under construction lies at its port: it may load already
        if (!docked(state, e)) return fail('notDocked');
        if (e.slbms + e.slbmQueued + intent.count > BALANCE.sub.slbms) return fail('full');
        if (p.credits < BALANCE.sub.slbmCost * intent.count) return fail('credits');
        return OK;
      }
      if (e.kind === 'sam') {
        if (!structureActive(state, e)) return fail('notReady');
        if (e.ammo + intent.count > BALANCE.sam.ammo) return fail('full');
        if (p.credits < BALANCE.sam.roundCost * intent.count) return fail('credits');
        return OK;
      }
      return fail('badKind');
    }

    case 'launch': {
      const e = owned(state, p.id, intent.launcherId);
      if (typeof e === 'string') return fail(e);
      if (!finite(intent.x, intent.y)) return fail('badIntent');
      if (e.kind === 'silo') {
        if (!structureActive(state, e)) return fail('notReady');
        if (e.loaded < 1) return fail('empty');
        if (state.tick < e.cooldownUntil) return fail('cooldown');
      } else if (e.kind === 'sub') {
        if (!shipActive(state, e)) return fail('notReady');
        if (e.slbms < 1) return fail('empty');
        if (dist(e.x, e.y, intent.x, intent.y) > BALANCE.sub.slbmRange) return fail('range');
      } else return fail('badKind');
      const err = strikeTargetError(state, p.id, intent.x, intent.y);
      if (err) return fail(err);
      if (inFlightCount(state, p.id) >= BALANCE.missiles.inFlightCap) return fail('inFlightCap');
      return OK;
    }

    case 'buildAircraft': {
      const e = owned(state, p.id, intent.baseId);
      if (typeof e === 'string') return fail(e);
      if (intent.kind !== 'fighter' && intent.kind !== 'bomber') return fail('badKind');
      if (e.kind === 'airbase') {
        if (!structureActive(state, e)) return fail('notReady');
      } else if (e.kind === 'carrier') {
        if (intent.kind !== 'fighter') return fail('badKind');
        if (!shipActive(state, e)) return fail('notReady');
      } else return fail('badKind');
      if (aircraftOf(state, e.id).length >= baseCapacity(e)) return fail('full');
      if (p.credits < BALANCE[intent.kind].cost) return fail('credits');
      return OK;
    }

    case 'air': {
      const e = owned(state, p.id, intent.baseId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'airbase' && e.kind !== 'carrier') return fail('badKind');
      if (!finite(intent.x, intent.y) || !inBounds(state, intent.x, intent.y)) return fail('badTarget');
      const { kind, mission } = intent;
      if (kind !== 'fighter' && kind !== 'bomber') return fail('badKind');
      if (mission === 'strike' ? kind !== 'bomber' : mission === 'patrol' ? kind !== 'fighter' : mission !== 'scout') return fail('badKind');
      const ready = aircraftOf(state, e.id).find((a) => a.kind === kind && a.phase === 'parked' && (mission !== 'strike' || a.ammo > 0));
      if (!ready) return fail('noAircraft');
      if (dist(e.x, e.y, intent.x, intent.y) > BALANCE[kind].radius) return fail('range');
      if (mission === 'strike') {
        const err = strikeTargetError(state, p.id, intent.x, intent.y);
        if (err) return fail(err);
      }
      return OK;
    }

    case 'airCmd': {
      const e = owned(state, p.id, intent.aircraftId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'fighter' && e.kind !== 'bomber') return fail('badKind');
      if (!isAirborne(e)) return fail('notReady');
      if (intent.cmd === 'recall') return OK;
      if (intent.cmd !== 'retarget') return fail('badIntent');
      if (!finite(intent.x, intent.y)) return fail('badIntent');
      const x = intent.x as number;
      const y = intent.y as number;
      if (!inBounds(state, x, y)) return fail('badTarget');
      const base = state.entities.get(e.baseId);
      if (!base || dist(base.x, base.y, x, y) > BALANCE[e.kind].radius) return fail('range');
      if (e.mission === 'strike' && e.ammo > 0) {
        const err = strikeTargetError(state, p.id, x, y);
        if (err) return fail(err);
      }
      return OK;
    }

    case 'move': {
      const e = owned(state, p.id, intent.unitId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'sub' && e.kind !== 'carrier') return fail('badKind');
      if (!shipActive(state, e)) return fail('notReady');
      if (!finite(intent.x, intent.y) || !inBounds(state, intent.x, intent.y)) return fail('badTarget');
      if (intent.queue !== undefined && typeof intent.queue !== 'boolean') return fail('badIntent');
      if (moveTarget(state, e, intent.x, intent.y) >= 0) return OK;
      // no water of the ship's own sea near the click: another sea (unreachable) or deep inland
      return fail(nearestWater(state.geo, state.world, intent.x, intent.y) >= 0 ? 'unreachable' : 'badTarget');
    }

    case 'autoScramble': {
      const e = owned(state, p.id, intent.baseId);
      if (typeof e === 'string') return fail(e);
      if (e.kind !== 'airbase' && e.kind !== 'carrier') return fail('badKind');
      return OK;
    }

    case 'ally.request': {
      if (state.settings.mode !== 'ffa') return fail('allianceMode');
      const to = player(state, intent.to);
      if (!to || to.eliminatedTick >= 0) return fail('unknownPlayer');
      if (to.id === p.id) return fail('self');
      const a = state.alliances;
      if (a.allyOf[p.id] === to.id) return fail('alreadyAllied');
      if (a.allyOf[p.id] || a.allyOf[to.id]) return fail('allianceSize');
      // either side still serving an alliance break: the offer could not be accepted
      if (state.tick < a.noAllyUntil[p.id] || state.tick < a.noAllyUntil[to.id]) return fail('allianceCooldown');
      if (a.requests.some((r) => r.from === p.id && r.to === to.id)) return fail('busy');
      return OK;
    }

    case 'ally.respond': {
      if (state.settings.mode !== 'ffa') return fail('allianceMode');
      const a = state.alliances;
      // declining is always fine (a request that vanished in the meantime needs no answer)
      if (!intent.accept) return OK;
      if (!a.requests.some((r) => r.from === intent.from && r.to === p.id)) return fail('noRequest');
      const from = player(state, intent.from);
      if (!from || from.eliminatedTick >= 0) return fail('unknownPlayer');
      if (a.allyOf[p.id] || a.allyOf[from.id]) return fail('allianceSize');
      if (state.tick < a.noAllyUntil[p.id] || state.tick < a.noAllyUntil[from.id]) return fail('allianceCooldown');
      return OK;
    }

    case 'ally.break': {
      if (state.settings.mode !== 'ffa') return fail('allianceMode');
      if (state.alliances.allyOf[p.id] !== intent.with) return fail('notAllied');
      return OK;
    }

    case 'ceasefire.request': {
      if (state.settings.mode !== 'ffa') return fail('allianceMode');
      const to = player(state, intent.to);
      if (!to || to.eliminatedTick >= 0) return fail('unknownPlayer');
      if (to.id === p.id) return fail('self');
      const a = state.alliances;
      if (a.allyOf[p.id] === to.id) return fail('alreadyAllied');
      if (inCeasefire(state, p.id, to.id)) return fail('ceasefire');
      if (state.tick < a.noCeasefireUntil[p.id] || state.tick < a.noCeasefireUntil[to.id]) return fail('ceasefireCooldown');
      if (a.ceasefireRequests.some((r) => r.from === p.id && r.to === to.id)) return fail('busy');
      return OK;
    }

    case 'ceasefire.respond': {
      if (state.settings.mode !== 'ffa') return fail('allianceMode');
      const a = state.alliances;
      if (!intent.accept) return OK;
      if (!a.ceasefireRequests.some((r) => r.from === intent.from && r.to === p.id)) return fail('noRequest');
      const from = player(state, intent.from);
      if (!from || from.eliminatedTick >= 0) return fail('unknownPlayer');
      if (a.allyOf[p.id] === from.id) return fail('alreadyAllied');
      if (inCeasefire(state, p.id, from.id)) return fail('ceasefire');
      if (state.tick < a.noCeasefireUntil[p.id] || state.tick < a.noCeasefireUntil[from.id]) return fail('ceasefireCooldown');
      return OK;
    }

    case 'ceasefire.break': {
      if (state.settings.mode !== 'ffa') return fail('allianceMode');
      if (!isInt(intent.with) || !inCeasefire(state, p.id, intent.with)) return fail('noCeasefire');
      return OK;
    }

    case 'gift': {
      const to = player(state, intent.to);
      if (!to || to.eliminatedTick >= 0) return fail('unknownPlayer');
      if (to.id === p.id) return fail('self');
      if (!BALANCE.diplomacy.giftAmounts.includes(intent.amount)) return fail('badIntent');
      if (p.credits < intent.amount) return fail('credits');
      return OK;
    }

    case 'mark': {
      const t = player(state, intent.target);
      if (!t || t.eliminatedTick >= 0) return fail('unknownPlayer');
      if (t.id === p.id) return fail('self');
      if (!state.players.some((q) => q.id !== p.id && q.eliminatedTick < 0 && allied(state, p.id, q.id))) return fail('notAllied');
      if (allied(state, p.id, t.id)) return fail('badTarget');
      if (state.tick < state.alliances.markReadyTick[p.id]) return fail('cooldown');
      return OK;
    }

    case 'surrender':
      return OK;

    default:
      return fail('badIntent');
  }
}

function validateBuild(state: MatchState, p: PlayerState, kind: BuildKind, hex: number): IntentResult {
  if (!BUILD_KINDS.includes(kind)) return fail('badKind');
  const w = state.world;
  if (!isInt(hex) || hex < 0 || hex >= w.landCount) return fail('badHex');
  if (state.owner[hex] !== p.id) return fail('notOwnHex');
  if (p.credits < buildCost(kind)) return fail('credits');
  if (countOwned(state, p.id, kind) >= buildCap(kind)) return fail('cap');
  if (kind === 'sub' || kind === 'carrier') {
    if (!w.coastal[hex] || w.portWater[hex] < 0) return fail('notPort');
    return OK;
  }
  if (state.structId[hex] >= 0) return fail('occupied');
  if (state.rad[hex] >= BALANCE.build.maxRad) return fail('radiation');
  if (kind === 'city') {
    for (const e of state.entities.values()) {
      if (e.kind === 'city' && hexDist(w, e.hex, hex) < BALANCE.city.minSpacing) return fail('spacing');
    }
  }
  return OK;
}
